import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Save, RotateCcw } from 'lucide-react';
import axios from 'axios';

const ImageEditor = ({ project, onSave }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [adjustments, setAdjustments] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    width: 1200
  });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadImage();
  }, [project]);

  const loadImage = () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = project.image;
    img.onload = () => {
      imageRef.current = img;
      applyFilters();
    };
  };

  const applyFilters = () => {
    if (!imageRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    // Calculate dimensions
    const ratio = Math.min(adjustments.width / img.width, 1);
    canvas.width = img.width * ratio;
    canvas.height = img.height * ratio;

    // Apply filters
    ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    applyFilters();
  }, [adjustments]);

  const handleReset = () => {
    setAdjustments({
      brightness: 100,
      contrast: 100,
      saturation: 100,
      width: 1200
    });
  };

  const handleSave = async () => {
    if (!canvasRef.current) return;
    
    setIsProcessing(true);
    try {
      // Convert canvas to blob
      const blob = await new Promise(resolve => {
        canvasRef.current.toBlob(resolve, 'image/jpeg', 0.92);
      });

      // Create form data
      const formData = new FormData();
      formData.append('image', blob, `${project.id}.jpg`);
      formData.append('projectId', project.id);
      formData.append('adjustments', JSON.stringify(adjustments));

      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(
        `${BACKEND_URL}/api/projects/${project.id}/update-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        alert('Image mise à jour avec succès!');
        if (onSave) {
          onSave({
            ...project,
            image: response.data.image
          });
        }
      }
    } catch (error) {
      console.error('Error saving image:', error);
      alert('Erreur lors de la sauvegarde de l\'image');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Aperçu de l'image</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-100 p-4 rounded-lg">
            <canvas 
              ref={canvasRef}
              className="w-full h-auto rounded shadow-lg"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ajustements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Luminosité</Label>
              <span className="text-sm text-slate-600">{adjustments.brightness}%</span>
            </div>
            <Slider
              value={[adjustments.brightness]}
              onValueChange={([value]) => setAdjustments({...adjustments, brightness: value})}
              min={0}
              max={200}
              step={1}
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Contraste</Label>
              <span className="text-sm text-slate-600">{adjustments.contrast}%</span>
            </div>
            <Slider
              value={[adjustments.contrast]}
              onValueChange={([value]) => setAdjustments({...adjustments, contrast: value})}
              min={0}
              max={200}
              step={1}
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Saturation (Intensité)</Label>
              <span className="text-sm text-slate-600">{adjustments.saturation}%</span>
            </div>
            <Slider
              value={[adjustments.saturation]}
              onValueChange={([value]) => setAdjustments({...adjustments, saturation: value})}
              min={0}
              max={200}
              step={1}
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Largeur maximale (px)</Label>
              <span className="text-sm text-slate-600">{adjustments.width}px</span>
            </div>
            <Slider
              value={[adjustments.width]}
              onValueChange={([value]) => setAdjustments({...adjustments, width: value})}
              min={400}
              max={2000}
              step={100}
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleReset} variant="outline" className="flex-1">
              <RotateCcw className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
            <Button 
              onClick={handleSave} 
              className="flex-1 bg-slate-900"
              disabled={isProcessing}
            >
              <Save className="w-4 h-4 mr-2" />
              {isProcessing ? 'Enregistrement...' : 'Sauvegarder'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ImageEditor;